import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { m, AnimatePresence } from 'framer-motion';
import { useAnalytics } from '../../hooks/useAnalytics';

interface NewsletterFormProps {
  source: string; // Where the form is located (e.g., 'footer', 'contact')
  variant?: 'inline' | 'stacked' | 'compact';
  showHeading?: boolean;
  headingText?: string;
  buttonText?: string;
  placeholder?: string;
  isDark?: boolean;
  autoFocus?: boolean;
  onSuccess?: () => void;
}

const FormContainer = styled.div<{ $variant: string }>`
  width: 100%;

  ${props =>
    props.$variant === 'compact' &&
    css`
      max-width: 320px;
    `}
`;

const Heading = styled.h4<{ $isDark: boolean }>`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => (props.$isDark ? 'rgba(255, 255, 255, 0.9)' : props.theme.colors.light.text)};
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const Form = styled.form<{ $variant: string }>`
  display: flex;
  gap: ${props => props.theme.spacing[2]};

  ${props =>
    props.$variant === 'stacked' &&
    css`
      flex-direction: column;
    `}

  ${props =>
    props.$variant === 'inline' &&
    css`
      flex-direction: row;

      @media (max-width: ${props.theme.breakpoints.sm}) {
        flex-direction: column;
      }
    `}

  ${props =>
    props.$variant === 'compact' &&
    css`
      flex-direction: column;
    `}
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const Input = styled.input<{ $isDark: boolean; $hasError: boolean }>`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.transitions.fast};

  ${props =>
    props.$isDark
      ? css`
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid ${props.$hasError ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'};
          color: white;

          &::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          &:focus {
            outline: none;
            border-color: ${props.$hasError ? '#ef4444' : props.theme.colors.primary};
            background: rgba(255, 255, 255, 0.15);
          }
        `
      : css`
          background: #f8fafc;
          border: 2px solid ${props.$hasError ? '#ef4444' : '#cbd5e1'};
          color: ${props.theme.colors.light.text};

          &::placeholder {
            color: #64748b;
          }

          &:focus {
            outline: none;
            background: white;
            border-color: ${props.$hasError ? '#ef4444' : props.theme.colors.primary};
            box-shadow: 0 0 0 3px rgba(10, 93, 235, 0.15);
          }
        `}
`;

const SubmitButton = styled(m.button)<{ $isDark: boolean; $isLoading: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: ${props => (props.$isLoading ? 'wait' : 'pointer')};
  transition: all ${props => props.theme.transitions.fast};
  border: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[2]};

  background: ${props => props.theme.colors.primary};
  color: white;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primaryDark || '#0947c9'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Message = styled(m.div)<{ $type: 'success' | 'error' }>`
  padding: ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  margin-top: ${props => props.theme.spacing[2]};

  ${props =>
    props.$type === 'success' &&
    css`
      background: rgba(34, 197, 94, 0.1);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.2);
    `}

  ${props =>
    props.$type === 'error' &&
    css`
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
      border: 1px solid rgba(239, 68, 68, 0.2);
    `}
`;

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const NewsletterForm: React.FC<NewsletterFormProps> = ({
  source,
  variant = 'inline',
  showHeading = false,
  headingText = 'Subscribe to our newsletter',
  buttonText = 'Subscribe',
  placeholder = 'Enter your email',
  isDark = true,
  autoFocus = false,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { trackEvent } = useAnalytics();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    // Track attempt (client-side for immediate feedback)
    trackEvent('newsletter', 'attempt', source);

    try {
      const response = await fetch('/api/v1/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          source,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: data.already_subscribed
            ? "You're already subscribed! Thanks for your interest."
            : 'Thanks for subscribing! Check your email to confirm.',
        });
        setEmail('');
        trackEvent('newsletter', 'success', source);
        onSuccess?.();
      } else if (data.fallback_url) {
        // Cloudflare blocked server-side request - open Substack directly
        trackEvent('newsletter', 'fallback', source);
        window.open(data.fallback_url, '_blank', 'noopener,noreferrer');
        setMessage({
          type: 'success',
          text: 'Opening Substack to complete your subscription...',
        });
        setEmail('');
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Something went wrong. Please try again.',
        });
        trackEvent('newsletter', 'error', source);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      trackEvent('newsletter', 'error', `${source}:network`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer $variant={variant}>
      {showHeading && <Heading $isDark={isDark}>{headingText}</Heading>}

      <Form $variant={variant} onSubmit={handleSubmit}>
        <InputWrapper>
          <Input
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              if (message) setMessage(null);
            }}
            placeholder={placeholder}
            $isDark={isDark}
            $hasError={message?.type === 'error'}
            disabled={isLoading}
            autoFocus={autoFocus}
            aria-label="Email address"
          />
        </InputWrapper>

        <SubmitButton
          type="submit"
          $isDark={isDark}
          $isLoading={isLoading}
          disabled={isLoading}
          whileHover={{ scale: isLoading ? 1 : 1.02 }}
          whileTap={{ scale: isLoading ? 1 : 0.98 }}
        >
          {isLoading ? (
            <>
              <Spinner />
              Subscribing...
            </>
          ) : (
            buttonText
          )}
        </SubmitButton>
      </Form>

      <AnimatePresence mode="wait">
        {message && (
          <Message
            $type={message.type}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {message.text}
          </Message>
        )}
      </AnimatePresence>
    </FormContainer>
  );
};

export default NewsletterForm;
